import { motion, useReducedMotion, useScroll, useTransform } from "motion/react";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { rateSummaryCards, resort } from "@/data/resort";
import { HeroPhoto } from "../OptimizedImage";
import { ResortLink } from "../resort-button";
import { Reveal } from "../reveal";

export function RatesHero() {
  const ref = useRef<HTMLElement>(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "10%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-10%"]);
  const ease = [0.22, 1, 0.36, 1] as const;

  return (
    <section
      ref={ref}
      id="top"
      aria-labelledby="rates-hero-heading"
      className="relative min-h-[78svh] overflow-hidden bg-forest-deep md:min-h-[70svh]"
    >
      <motion.div
        className="absolute inset-0"
        style={{ y: reduced ? 0 : bgY }}
        initial={reduced ? false : { scale: 1.04 }}
        animate={{ scale: 1 }}
        transition={{ duration: 2.2, ease }}
      >
        <HeroPhoto alt="Spacious RV sites and oak trees at Auburn RV Resort in the Sierra Foothills" />
        <div className="overlay-cinematic absolute inset-0" />
      </motion.div>

      <motion.div
        style={{ y: reduced ? 0 : contentY }}
        className="shell relative flex min-h-[78svh] flex-col justify-end pb-36 pt-18 md:min-h-[70svh] md:pb-36 md:pt-20"
      >
        <motion.p
          className="eyebrow text-primary-foreground/80"
          initial={reduced ? false : { opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2, ease }}
        >
          Stay Your Way
        </motion.p>

        <h1
          id="rates-hero-heading"
          className="display-xl mt-6 max-w-[16ch] text-primary-foreground"
        >
          {["Flexible RV Rates", "for Every Kind", "of Stay"].map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span
                className="display-xl block"
                initial={reduced ? false : { y: "112%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.1, delay: 0.35 + i * 0.1, ease }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          className="mt-8 max-w-2xl text-base leading-relaxed text-primary-foreground/80 md:text-lg"
          initial={reduced ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85, ease }}
        >
          Stopping for the night, exploring the Sierra Foothills for a week or planning a longer
          stay? Auburn RV Resort offers flexible daily, weekly and monthly options designed around
          your journey.
        </motion.p>

        <motion.div
          className="mt-10 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:flex-wrap"
          initial={reduced ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05, ease }}
        >
          <ResortLink variant="gold" href="/reservations/" className="w-full sm:w-auto">
            Check Availability
          </ResortLink>
          <ResortLink variant="onDark" href={resort.phoneHref} className="w-full sm:w-auto">
            Call for Current Rates
          </ResortLink>
        </motion.div>

        <motion.p
          className="eyebrow mt-6 text-primary-foreground/60"
          initial={reduced ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.2, ease }}
        >
          Rates vary by site size, location and availability.
        </motion.p>
      </motion.div>

      <div className="pointer-events-none absolute bottom-48 right-6 hidden items-center gap-3 text-primary-foreground/70 md:flex md:bottom-44">
        <span className="eyebrow">Scroll</span>
        <motion.span
          animate={reduced ? {} : { y: [0, 8, 0] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown className="size-4" strokeWidth={1.5} />
        </motion.span>
      </div>

      <div className="absolute inset-x-0 bottom-0 z-10 translate-y-[18%] md:translate-y-[22%]">
        <div className="shell pb-[4.5rem] md:pb-0">
          <div className="overflow-hidden rounded-sm border border-border/60 bg-card/95 shadow-soft backdrop-blur-md">
            <ul className="grid gap-0 md:grid-cols-[1fr_1fr_1fr_auto]">
              {rateSummaryCards.map((card, i) => (
                <Reveal key={card.id} delay={i * 0.08}>
                  <li className="border-border/60 px-6 py-5 text-center md:border-r md:px-7 md:py-6 md:text-left [&:not(:last-child)]:border-b md:[&:not(:last-child)]:border-b-0">
                    <p className="eyebrow text-muted-foreground">{card.label}</p>
                    <p className="font-display mt-2 text-3xl tracking-tight text-primary md:text-4xl">
                      From {card.from}
                    </p>
                    <p className="mt-1.5 text-sm text-muted-foreground">{card.copy}</p>
                  </li>
                </Reveal>
              ))}
              <Reveal
                delay={0.24}
                className="flex items-center justify-center border-t border-border/60 px-5 py-5 md:border-t-0 md:px-6"
              >
                <ResortLink
                  variant="solid"
                  href="/reservations/"
                  className="w-full whitespace-nowrap"
                >
                  Check Your Dates
                </ResortLink>
              </Reveal>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
